import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createTranslator, resolveLocale, type LocalePreference } from '@physical-movies/core';
import { I18nContext } from './context';
import { SETTINGS_KEYS, readSetting, writeSetting } from '../lib/storage';

const PREFERENCES: readonly LocalePreference[] = ['system', 'en', 'de'];

export function I18nProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<LocalePreference>(() =>
    readSetting(SETTINGS_KEYS.language, PREFERENCES, 'system'),
  );

  // English unless the browser's primary language is German.
  const locale = resolveLocale(preference, navigator.languages ?? [navigator.language]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setPreference = useCallback((next: LocalePreference) => {
    setPreferenceState(next);
    writeSetting(SETTINGS_KEYS.language, next);
  }, []);

  const value = useMemo(
    () => ({ t: createTranslator(locale), locale, preference, setPreference }),
    [locale, preference, setPreference],
  );

  return <I18nContext value={value}>{children}</I18nContext>;
}
